import React from 'react'
import { useGetBusiness } from '@marketing-milk/frontend'
import { AdCreative, AssetApprovalStatus, LeadCard } from '@marketing-milk/interfaces'
import { EmptyLeadCardId } from 'components'
import { AdCreativeList } from 'pages/business/campaign-builder/steps/ad-creative/ad-creative-list/AdCreativeList'
import { LeadCardPreview } from 'pages/business/lead-cards/lead-card-preview/LeadCardPreview'
import { leadCardDTOWire } from 'pages/business/lead-cards/wizard/lead-card.wire'

type Props = {
  campaignName?: string
  creatives?: AdCreative[]
  declaredForms: LeadCard[]
  activeFormId: number
  setActiveFormId: (id: number) => void
  selectedCreatives: AdCreative[]
  selectCreative: (creative: AdCreative) => void
  selectAll: (visible: AdCreative[]) => void
  deselectAll: (visible: AdCreative[]) => void
  onSelectLeadless: (visible: AdCreative[]) => void
  onAssign: () => void
  assigning: boolean
  onUnassign: () => void
  unassigning: boolean
  onAISort: () => void
  sorting: boolean
}

export const AssignLeadFormsStep = ({
  campaignName,
  creatives,
  declaredForms,
  activeFormId,
  setActiveFormId,
  selectedCreatives,
  selectCreative,
  selectAll,
  deselectAll,
  onSelectLeadless,
  onAssign,
  assigning,
  onUnassign,
  unassigning,
  onAISort,
  sorting,
}: Props) => {
  const business = useGetBusiness()
  const unassignedCount = (creatives ?? []).filter(c => !c.leadCardUsed).length
  const countForForm = (form: LeadCard) =>
    (creatives ?? []).filter(c => c.leadCardUsed?.id === form.id).length
  const activeForm = declaredForms.find(f => f.id === activeFormId)
  // Undo for a bad assignment: send just the selected ads back to Unassigned, regardless of
  // which form is active. Only meaningful when at least one selected ad actually carries a form.
  const selectedHaveAForm = selectedCreatives.some(c => c.leadCardUsed)
  // Nothing selected is the normal motion — hand-place the few that are obvious, let the sort
  // take the rest — so the button falls back to every unassigned ad rather than going dead.
  const sortsSelection = selectedCreatives.length > 0
  const alreadyAssignedInSelection = selectedCreatives.filter(c => c.leadCardUsed).length
  const sortCount = sortsSelection ? selectedCreatives.length : unassignedCount

  const chipClasses = (active: boolean, unassigned?: boolean) =>
    `rounded-full px-3 py-1.5 text-sm font-semibold border transition-colors ${
      active
        ? unassigned
          ? 'bg-gray-800 text-white border-gray-800'
          : 'bg-purple-700 text-white border-purple-700'
        : unassigned
        ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        : 'bg-white text-purple-700 border-purple-600 hover:bg-purple-50'
    }`

  // Deliberately NOT wrapped in BlankPageLayout: its content area centers children with
  // `items-center`, which lets a block child shrink to its content's natural width instead of
  // stretching — fine for short text, but it broke AdCreativeList's fixed-width card grid (rows
  // overlapped once nested here). Plain block markup keeps every width explicit.
  //
  // The panel is a flex sibling of the list rather than a popover over it, so nothing on this
  // screen is ever covered and nothing reflows when the active form changes.
  //
  // Everything that is the job — title, forms, filters, the ads, the actions — lives in one
  // column. The panel holds the preview and nothing else: the forms used to stack down it, and
  // at five or six declared forms that pushed the preview off the bottom of the drawer.
  return (
    <div className="w-full p-4">
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="w-full flex-1 min-w-0">
          <h2 className="text-lg font-bold">
            {campaignName ? `Assign Ads To Forms — ${campaignName}` : 'Assign Ads To Forms'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Select the ads below, then pick a form to attach it to all of them at once. Published
            ads are locked — fix those in Ads Manager instead.
          </p>

          {/* Above the ads rather than down the panel: the counts are the completeness check,
              so they belong next to what they are counting, and laid out in a row they cost one
              or two lines however many forms the batch declared. */}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={() => setActiveFormId(EmptyLeadCardId)}
              className={chipClasses(activeFormId === EmptyLeadCardId, true)}
            >
              Unassigned ({unassignedCount})
            </button>
            {declaredForms.map(form => (
              <button
                key={form.id}
                type="button"
                onClick={() => setActiveFormId(form.id)}
                className={chipClasses(activeFormId === form.id)}
              >
                {form.formName} ({countForForm(form)})
              </button>
            ))}
          </div>

          <AdCreativeList
            creatives={creatives}
            selectedCreatives={selectedCreatives}
            selectCreative={selectCreative}
            onSelectAll={selectAll}
            onDeselectAll={deselectAll}
            onSelectLeadless={onSelectLeadless}
            isHighlighted={c =>
              activeFormId === EmptyLeadCardId
                ? !c.leadCardUsed
                : c.leadCardUsed?.id === activeFormId
            }
            disableSelect={c => c.reviewStatus === AssetApprovalStatus.Published}
            defaultViewMode="list"
            showCreativeLetters={false}
          />

          {/* Sticky rather than at the natural end of the scrolling content — with a batch of
              50 ads the button used to scroll out of reach below the list. `bottom-0` sticks
              it to the bottom of AssignLeadFormsStep's scrolling ancestor (Stepper's own
              StepContent), landing it right above the Stepper's Previous/Done footer. It
              spans the list only: stretched across the panel too, it would clip the preview. */}
          <div className="sticky bottom-0 z-10 mt-4 border-t bg-white py-3 flex items-center justify-center gap-3">
            <button
              className="rounded-md px-4 py-2 font-semibold text-sm bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onAssign}
              disabled={
                assigning || activeFormId === EmptyLeadCardId || selectedCreatives.length === 0
              }
            >
              {assigning
                ? 'Assigning...'
                : `Assign ${activeForm ? `"${activeForm.formName}"` : 'Form'} to ${
                    selectedCreatives.length
                  } Selected Ad${selectedCreatives.length === 1 ? '' : 's'}`}
            </button>
            {/* The label is the whole disclosure — there is no confirmation step, and a sort
                that includes already-assigned ads overwrites hand-placed work with nothing to
                undo it. So it names that count before you press, and never rounds it off. */}
            <button
              className="rounded-md px-4 py-2 font-semibold text-sm bg-white text-purple-700 border border-purple-600 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onAISort}
              disabled={sorting || sortCount === 0 || declaredForms.length === 0}
            >
              {sorting
                ? 'Sorting...'
                : sortsSelection
                ? `AI sort ${sortCount} selected${
                    alreadyAssignedInSelection > 0
                      ? ` — ${alreadyAssignedInSelection} already assigned`
                      : ''
                  }`
                : `AI sort ${sortCount} unassigned`}
            </button>
            <button
              className="rounded-md px-4 py-2 font-semibold text-sm bg-white text-gray-700 border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onUnassign}
              disabled={unassigning || !selectedHaveAForm}
            >
              {unassigning
                ? 'Removing...'
                : `Remove Lead Form From ${selectedCreatives.length} Selected Ad${
                    selectedCreatives.length === 1 ? '' : 's'
                  }`}
            </button>
          </div>
        </div>

        {/* On wider screens, sticky so the form stays beside the rows being scrolled: the list runs to fifty ads
            and the point of the panel is checking a row against the form it landed on. On the
            right to match the lead-card builder, which is form-left preview-right and which
            step one of this same drawer embeds. Below lg, stack the preview after the ads
            so the list has enough room to keep ad names readable. */}
        <aside className="w-full lg:w-[360px] lg:flex-shrink-0 lg:sticky lg:top-4">
          <div className="rounded-lg border bg-white p-3">
            {activeForm ? (
              <>
                <p className="font-semibold text-sm text-gray-600 mb-2 truncate">
                  {activeForm.formName}
                </p>
                <LeadCardPreview
                  leadCard={leadCardDTOWire(activeForm)}
                  business={business}
                  mobilePreview={false}
                  selectionPreview={false}
                  showName={false}
                />
              </>
            ) : (
              <p className="text-sm text-gray-500">
                These ads don&apos;t have a form yet. Pick one to preview it.
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  )
}
